import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Shield, Users, FileText, Newspaper, BarChart3, Settings,
  Share2, Activity, Bell, Loader2, AlertCircle, Send, Webhook, RotateCcw, Film, Handshake, Target, Clapperboard
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { useAuth } from '@/context/AuthContext';

import OverviewTab from '@/pages/admin/OverviewTab';
import AnalyticsTab from '@/pages/admin/AnalyticsTab';
import UserDatabaseTab from '@/pages/admin/UserDatabaseTab';
import NotificationsTab from '@/pages/admin/NotificationsTab';
import UsersTab from '@/pages/admin/UsersTab';
import NewsTab from '@/pages/admin/NewsTab';
import BlogTab from '@/pages/admin/BlogTab';
import SocialTab from '@/pages/admin/SocialTab';
import LeadsTab from '@/pages/admin/LeadsTab';
import ContentDistributionTab from '@/pages/admin/ContentDistributionTab';
import NIAIntegrationTab from '@/pages/admin/NIAIntegrationTab';
import HomepageRotationTab from '@/pages/admin/HomepageRotationTab';
import VideoTab from '@/pages/admin/VideoTab';
import MediaLibraryTab from '@/pages/admin/MediaLibraryTab';
import PartnersTab from '@/pages/admin/PartnersTab';
import CampaignsTab from '@/pages/admin/CampaignsTab';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'analytics', label: 'Analytics', icon: Activity },
  { id: 'userdb', label: 'User Database', icon: Users },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'users', label: 'Roles & Access', icon: Shield },
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'blog', label: 'Blog', icon: FileText },
  { id: 'campaigns', label: 'Campaigns', icon: Target },
  { id: 'social', label: 'Social Media', icon: Share2 },
  { id: 'videos', label: 'Videos', icon: Film },
  { id: 'media', label: 'Media library', icon: Clapperboard },
  { id: 'leads', label: 'Leads', icon: Settings },
  { id: 'distribution', label: 'Distribution', icon: Send },
  { id: 'nia', label: 'NIA Integration', icon: Webhook },
  { id: 'rotation', label: 'Homepage', icon: RotateCcw },
  { id: 'partners', label: 'Partners', icon: Handshake },
];

const AUTH_HEADERS = { 'Content-Type': 'application/json' };

const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab') || 'overview';
  const [tab, setTab] = useState(initialTab);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [myRole, setMyRole] = useState('viewer');

  const headers = AUTH_HEADERS;

  useEffect(() => {
    if (!authLoading && !user) { navigate('/login', { replace: true }); return; }
    if (user) {
      // Fetch admin role
      (async () => {
        try {
          const res = await fetch(`${API}/api/admin/me`, { headers, credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setMyRole(data.role);
            // Then fetch stats
            try {
              const sRes = await fetch(`${API}/api/admin/stats`, { headers, credentials: 'include' });
              if (sRes.ok) setStats(await sRes.json());
              else { const d = await sRes.json().catch(() => ({})); setError(d.detail || 'Failed to load admin data.'); }
            } catch { setError('Connection error'); }
            finally { setLoading(false); }
          } else {
            const data = await res.json().catch(() => ({}));
            setError(data.detail || "You don't have admin access. Contact the account owner to be added.");
            setLoading(false);
          }
        } catch {
          setError('Connection error');
          setLoading(false);
        }
      })();
    }
  }, [user, authLoading, navigate, headers]);

  if (authLoading || loading) {
    return <PageLayout><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-taxera-blue" /></div></PageLayout>;
  }

  if (error) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-taxera-charcoal mb-2">Access Denied</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-portal">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-taxera-charcoal" data-testid="admin-title">Admin Portal</h1>
            <p className="text-sm text-slate-500">Manage content, users, and leads</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full">
            <Shield className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-600">{user?.email}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 overflow-x-auto pb-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                tab === id ? 'bg-taxera-charcoal text-white' : 'text-slate-500 hover:bg-slate-100'
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'overview' && <OverviewTab stats={stats} />}
        {tab === 'analytics' && <AnalyticsTab headers={headers} />}
        {tab === 'userdb' && <UserDatabaseTab headers={headers} />}
        {tab === 'notifications' && <NotificationsTab headers={headers} />}
        {tab === 'users' && <UsersTab headers={headers} myRole={myRole} />}
        {tab === 'news' && <NewsTab headers={headers} />}
        {tab === 'blog' && <BlogTab headers={headers} />}
        {tab === 'campaigns' && <CampaignsTab headers={headers} />}
        {tab === 'social' && <SocialTab headers={headers} />}
        {tab === 'videos' && <VideoTab headers={headers} />}
        {tab === 'media' && <MediaLibraryTab headers={headers} />}
        {tab === 'leads' && <LeadsTab headers={headers} />}
        {tab === 'distribution' && <ContentDistributionTab headers={headers} />}
        {tab === 'nia' && <NIAIntegrationTab headers={headers} />}
        {tab === 'rotation' && <HomepageRotationTab headers={headers} />}
        {tab === 'partners' && <PartnersTab headers={headers} />}
      </div>
    </PageLayout>
  );
};

export default AdminPage;
